import { Sentry } from 'lib-shared-sentry';

/**
 * This is a temporary patch to make the app more resilient against google
 * translate modifying the the DOM which makes react-dom crash when it tries to
 * update the same DOM nodes.
 *
 * @see https://github.com/facebook/react/issues/11538#issuecomment-417504600 The actual root cause of this is in chromium itself:
 * @see https://bugs.chromium.org/p/chromium/issues/detail?id=872770 once this is fixed we can remove this function
 */
export function patchAgainstGoogleTranslateInterferingWithReactDOM(
  errorLevel: 'fatal' | 'error' | 'warning' | 'log' | 'info' | 'debug',
) {
  const logErrorToSentry = (message: string, extra?: Record<string, any>) => {
    Sentry.captureMessage(message, {
      level: errorLevel,
      extra,
    });
  };

  if (typeof Node !== 'function' || !Node.prototype) {
    return;
  }

  // patch the removeChild method on html nodes

  const originalRemoveChild = Node.prototype.removeChild;
  Node.prototype.removeChild = function removeChildWrapper<
    T extends Node | null,
  >(this: ParentNode | null, child: T): T {
    if (child === null || child.parentNode !== this) {
      const errorMessage = 'Cannot remove a child from a different parent';
      const errorExtraInfo = {
        childNode: child,
        parentNode: this,
      };

      logErrorToSentry(errorMessage, errorExtraInfo);
      console?.warn?.(errorMessage, errorExtraInfo);
      return child;
    }

    const result = originalRemoveChild.apply(this, [child] as [Node]);
    return result as T;
  };

  // patch the insertBefore method on html nodes

  const originalInsertBefore = Node.prototype.insertBefore;
  Node.prototype.insertBefore = function insertBeforeWrapper<T extends Node>(
    this: Node | null,
    newNode: T,
    referenceNode: Node | null,
  ): T {
    if (referenceNode && referenceNode.parentNode !== this) {
      const errorMessage =
        'Cannot insert before a reference node from a different parent';

      const errorExtraInfo = {
        newNode: newNode,
        referenceNode,
        thisNode: this,
      };

      logErrorToSentry(errorMessage, errorExtraInfo);
      console?.warn?.(errorMessage, errorExtraInfo);
      return newNode;
    }

    const result = originalInsertBefore.apply(this, [newNode, referenceNode]);
    return result as T;
  };
}
